import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/common/CustomDialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { formatVersion } from '@/utils/versionsHelper';
import { versionToQuarter, getQuarterlyVersions } from '@/utils/versionUtils';

export interface PageScrape {
  version: string;
  quarter?: string | null;
  screenshot_url?: string | null;
  markdown_url?: string | null;
  html_url?: string | null;
  verified?: boolean | null;
}

interface ScreenshotModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  scrapes: PageScrape[];
  initialVersion?: string | null;
  title?: string;
}

const ScreenshotModal: React.FC<ScreenshotModalProps> = ({
  isOpen,
  onOpenChange,
  scrapes,
  initialVersion = null,
  title
}) => {
  const versions = useMemo(() => {
    const unique = Array.from(new Set(scrapes.map(s => s.version).filter(Boolean)));
    unique.sort((a, b) => b.localeCompare(a));
    if (unique.length === 0) return [] as string[];
    const quarterly = getQuarterlyVersions(unique);
    const latest = unique[0];
    const rest = quarterly.filter(v => v !== latest);
    return [latest, ...rest];
  }, [scrapes]);

  const [activeVersion, setActiveVersion] = useState<string | null>(null);

  useEffect(() => {
    if (initialVersion && versions.includes(initialVersion)) {
      setActiveVersion(initialVersion);
    } else if (versions.length > 0) {
      setActiveVersion(versions[0]);
    } else {
      setActiveVersion(null);
    }
  }, [initialVersion, versions]);

  // Navigate to previous version
  const navigatePrevious = useCallback(() => {
    if (!activeVersion || versions.length <= 1) return;
    
    const currentIndex = versions.indexOf(activeVersion);
    if (currentIndex > 0) {
      setActiveVersion(versions[currentIndex - 1]);
    }
  }, [activeVersion, versions]);

  // Navigate to next version
  const navigateNext = useCallback(() => {
    if (!activeVersion || versions.length <= 1) return;
    
    const currentIndex = versions.indexOf(activeVersion);
    if (currentIndex < versions.length - 1) {
      setActiveVersion(versions[currentIndex + 1]);
    }
  }, [activeVersion, versions]);

  // Add keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        navigatePrevious();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        navigateNext();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, navigatePrevious, navigateNext]);

  const activeScrape = useMemo(() => {
    if (!activeVersion) return null;
    return scrapes.find(s => s.version === activeVersion) || null;
  }, [scrapes, activeVersion]);

  const screenshotUrl = activeScrape?.screenshot_url || null;
  const htmlUrl = activeScrape?.html_url || null;
  const markdownUrl = activeScrape?.markdown_url || null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] w-[1000px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <button 
                onClick={navigatePrevious}
                disabled={versions.length <= 1 || versions.indexOf(activeVersion || '') <= 0}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1"
                title="Previous version (Left arrow key)"
              >
                ← 
              </button>
              
              <select
                className="border p-1 rounded"
                value={activeVersion || ''}
                onChange={e => setActiveVersion(e.target.value)}
                disabled={versions.length <= 1}
              >
                {versions.map(v => (
                  <option key={v} value={v}>
                    {formatVersion(v)} {versionToQuarter(v) ? `(${versionToQuarter(v)})` : ''}
                  </option>
                ))}
              </select>
              
              <button
                onClick={navigateNext}
                disabled={versions.length <= 1 || versions.indexOf(activeVersion || '') >= versions.length - 1}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1"
                title="Next version (Right arrow key)"
              >
                →
              </button>
            </div>
            <div className="flex space-x-4 text-sm">
              {htmlUrl && (
                <a href={htmlUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  View HTML
                </a>
              )}
              {markdownUrl && (
                <a href={markdownUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  View Markdown
                </a>
              )}
            </div>
          </div>

          <div className="overflow-y-auto max-h-[70vh] border rounded-md">
            {screenshotUrl ? (
              <img src={screenshotUrl} alt={`Screenshot ${activeVersion}`} className="max-w-full object-contain" />
            ) : (
              <div className="text-sm text-gray-500 p-6 text-center">No screenshot available.</div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center mt-4">
          <div className="flex items-center gap-4">
            {screenshotUrl && (
              <a
                href={screenshotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-4 w-4" /> Open in new tab
              </a>
            )}
            
            {activeVersion && (
              <div className="flex items-center gap-1">
                {activeScrape?.verified ? (
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-1" /> 
                    Verified
                  </div>
                ) : (
                  <div className="flex items-center text-gray-500 text-sm">
                    <XCircle className="h-4 w-4 mr-1" /> 
                    Not verified
                  </div>
                )}
              </div>
            )}
          </div>
          
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScreenshotModal;
